import {
	CodableConcept,
	CodableConceptCode,
	Coding,
	isKnownCodableConcept,
	isUnknownCodableConceptCode,
} from "./concept";
import { DataValueOrAbsent, DataValueOrAbsentCode, isDataValueAbsent, isDataValueAbsentCode } from "./data";
import { SupportedLocale } from "./i18n";
import { findConceptByCode, LocaleValueSetBundle } from "./value-set";

/**
 * Common interface for functions that can transform data into display strings.
 *
 * Even though not all transformation will need the locale, using the same interface for all these functions will make
 * it easier to compose and use them together.
 *
 * These "to display" functions should keep optional or explicit empty values in mind, which means that the value can be
 * `undefined` or `null`, so they are easier to use. Currently, these 2 "empty" cases are of the same meaning, and
 * rendered with `-`.
 */
export type DataToDisplay<T> = (data: T, locale: SupportedLocale) => string;

/** Gets the display for a coding. */
export function codingToDisplay(coding: Coding | null | undefined) {
	return coding ? coding.display : "-";
}

/** Gets the display for a codable concept, either known or unknown. */
export function codableConceptToDisplay(concept: CodableConcept | null | undefined) {
	return concept ? (isKnownCodableConcept(concept) ? concept.coding.display : concept.text) : "-";
}

/** Gets the display for a codable concept code, either known or unknown. */
export function codableConceptCodeToDisplayWith(
	codeToDisplay: DataToDisplay<string>,
): DataToDisplay<CodableConceptCode | null | undefined> {
	return function codableConceptCodeToDisplay(concept, locale) {
		return concept ? (isUnknownCodableConceptCode(concept) ? concept.text : codeToDisplay(concept.code, locale)) : "-";
	};
}

/** Gets the display for the data, either its absent reason or its actual display value. */
export function dataValueOrAbsentToDisplayWith<T>(
	valueToDisplay: DataToDisplay<NonNullable<T>>,
): DataToDisplay<DataValueOrAbsent<T> | null | undefined> {
	return function dataValueOrAbsentToDisplay(data, locale) {
		return data
			? isDataValueAbsent(data)
				? data.dataAbsentReason.coding.display
				: valueToDisplay(data.value, locale)
			: "-";
	};
}

/** Gets the display for the data, either its absent reason code or its actual display value. */
export function dataValueOrAbsentCodeToDisplayWith<T>(map: {
	absentCodeToDisplay: DataToDisplay<string>;
	valueToDisplay: DataToDisplay<NonNullable<T>>;
}): DataToDisplay<DataValueOrAbsentCode<T> | null | undefined> {
	return function dataValueOrAbsentCodeToDisplay(data, locale) {
		return data
			? isDataValueAbsentCode(data)
				? map.absentCodeToDisplay(data.dataAbsentReason.code, locale)
				: map.valueToDisplay(data.value, locale)
			: "-";
	};
}

/**
 * Utility function to transform an array of values into a single display value.
 *
 * This handles no array value and an empty array as "no values" and returns the '-' string.
 */
export function arrayToDisplayWith<T>(
	itemToDisplay: DataToDisplay<NonNullable<T>>,
): DataToDisplay<NonNullable<T>[] | null | undefined> {
	return function arrayToDisplay(array, locale) {
		return array && array.length > 0 ? array.map((item) => itemToDisplay(item, locale)).join(" | ") : "-";
	};
}

/**
 * Gets the display for a code from the given value set, returns the code if not found in the value set.
 *
 * As we're not using codings everywhere, we don't have the display value within our data, which is OK. We do know the
 * bound value set for a property, and thus are able to find the concept coding based on the code, and the bound value
 * set, and thus get the display value. If for some reason the code isn't found in the given value set, we simply return
 * the code itself. This is OK to do, because this should only happen when the value set is out-of-date with the source
 * data, and this makes our front-ends resilient so the user can keep working.
 */
export function codeToDisplayFromValueSetBundle<VSB extends LocaleValueSetBundle>(
	valueSetBundle: VSB,
): DataToDisplay<string | null | undefined> {
	return function codeToDisplay(code, locale) {
		return code ? (findConceptByCode(valueSetBundle[locale], code)?.coding.display ?? code) : "-";
	};
}
